// src/store/slices/stagesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/apiService";
import { RECON } from "../../services/apiConstant";

// Async thunk for fetching possible stages
// API endpoint: GET {{base_url}}/stages
export const getPossibleStages = createAsyncThunk(
  "stages/fetchPossible",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get(RECON.possibleStages);
      console.log("✅ Possible stages response:", response);
      return response;
    } catch (error) {
      console.error("❌ Fetch possible stages error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for deleting a provider
// API endpoint: DELETE {{base_url}}/stages/:stage_id
export const deleteStageConfig = createAsyncThunk(
  "stages/delete",
  async (stageId, { rejectWithValue }) => {
    try {
      const response = await apiService.delete(RECON.deleteStage(stageId));
      console.log("✅ Delete stage response:", response);
      return { stageId, response };
    } catch (error) {
      console.error("❌ Delete stage error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for updating an existing provider in-place
// API endpoint: PUT {{base_url}}/stages/:stage_id
export const updateStageConfig = createAsyncThunk(
  "stages/update",
  async ({ stageId, ...stageData }, { rejectWithValue }) => {
    try {
      const response = await apiService.put(RECON.updateStage(stageId), stageData);
      console.log("✅ Update stage response:", response);
      return response;
    } catch (error) {
      console.error("❌ Update stage error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for creating a new provider
// API endpoint: POST {{base_url}}/stages
export const createStageConfig = createAsyncThunk(
  "stages/create",
  async (stageData, { rejectWithValue }) => {
    try {
      const response = await apiService.post(RECON.possibleStages, stageData);
      console.log("✅ Create stage response:", response);
      return response;
    } catch (error) {
      console.error("❌ Create stage error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const stagesSlice = createSlice({
  name: "stages",
  initialState: {
    possibleStages: [],
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    deleteStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
  },
  reducers: {
    clearStages(state) {
      state.possibleStages = [];
      state.status = "idle";
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
    resetDeleteStatus(state) {
      state.deleteStatus = "idle";
    },
    resetCreateStatus(state) {
      state.createStatus = "idle";
    },
    resetUpdateStatus(state) {
      state.updateStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Get possible stages
      .addCase(getPossibleStages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getPossibleStages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.possibleStages = action.payload;
      })
      .addCase(getPossibleStages.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch stages";
      })
      // Delete stage
      .addCase(deleteStageConfig.pending, (state) => {
        state.deleteStatus = "loading";
      })
      .addCase(deleteStageConfig.fulfilled, (state) => {
        state.deleteStatus = "succeeded";
      })
      .addCase(deleteStageConfig.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload || "Failed to delete stage";
      })
      // Create stage
      .addCase(createStageConfig.pending, (state) => {
        state.createStatus = "loading";
      })
      .addCase(createStageConfig.fulfilled, (state) => {
        state.createStatus = "succeeded";
      })
      .addCase(createStageConfig.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.payload || "Failed to create stage";
      })
      // Update stage
      .addCase(updateStageConfig.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateStageConfig.fulfilled, (state) => {
        state.updateStatus = "succeeded";
      })
      .addCase(updateStageConfig.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload || "Failed to update stage";
      });
  },
});

export const { clearStages, clearError, resetDeleteStatus, resetCreateStatus, resetUpdateStatus } =
  stagesSlice.actions;

// Selectors
export const selectPossibleStages = (state) => state.stages.possibleStages;
export const selectStagesStatus = (state) => state.stages.status;
export const selectStagesError = (state) => state.stages.error;
export const selectDeleteStatus = (state) => state.stages.deleteStatus;
export const selectCreateStatus = (state) => state.stages.createStatus;
export const selectUpdateStatus = (state) => state.stages.updateStatus;

export default stagesSlice.reducer;
